const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000")
  .replace(/^http/, "ws");

let socket = null;
let reconnectTimer = null;
let currentToken = null;
let reconnectDelay = 1000;
const MAX_DELAY = 30000;
let wsConnected = false;

// listeners: Map<type, Set<callback>>
const listeners = new Map();
const connectionListeners = new Set();
/** group -> refcount of React subscribers that want this page-active group */
const groupRefs = new Map();
const activeGroups = new Set();

function setConnected(next) {
  if (wsConnected === next) return;
  wsConnected = next;
  connectionListeners.forEach((cb) => cb(next));
}

export function isWSConnected() {
  return wsConnected;
}

export function onWSConnectionChange(cb) {
  connectionListeners.add(cb);
  cb(wsConnected);
  return () => connectionListeners.delete(cb);
}

function _notify(type, event) {
  const cbs = listeners.get(type);
  if (cbs) cbs.forEach((cb) => cb(event));
  const all = listeners.get("*");
  if (all) all.forEach((cb) => cb(event));
}

export function sendWS(payload) {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false;
  try {
    socket.send(typeof payload === "string" ? payload : JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

function _flushActiveGroups() {
  activeGroups.forEach((group) => {
    sendWS({ type: "group_activate", group });
  });
}

export function activateWsGroup(group) {
  const key = String(group || "").trim().toLowerCase();
  if (!key) return () => {};
  const n = (groupRefs.get(key) || 0) + 1;
  groupRefs.set(key, n);
  if (n === 1) {
    activeGroups.add(key);
    sendWS({ type: "group_activate", group: key });
  }
  return () => deactivateWsGroup(key);
}

export function deactivateWsGroup(group) {
  const key = String(group || "").trim().toLowerCase();
  if (!key) return;
  const n = (groupRefs.get(key) || 0) - 1;
  if (n > 0) {
    groupRefs.set(key, n);
    return;
  }
  groupRefs.delete(key);
  activeGroups.delete(key);
  sendWS({ type: "group_deactivate", group: key });
}

function _connect(token) {
  if (socket && socket.readyState < 2) return; // OPEN or CONNECTING
  const url = `${WS_BASE}/ws?token=${encodeURIComponent(token)}`;
  const ws = new WebSocket(url);
  socket = ws;

  ws.onopen = () => {
    reconnectDelay = 1000;
    setConnected(true);
    _notify("__ws_connected__", { type: "__ws_connected__", action: "connected" });
    _flushActiveGroups();
    ws._pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send("ping");
      }
    }, 25000);
  };

  ws.onmessage = (e) => {
    if (e.data === "pong") return;
    try {
      const event = JSON.parse(e.data);
      if (event && event.type) _notify(event.type, event);
    } catch (_) {
      // ignore non-JSON frames
    }
  };

  ws.onclose = () => {
    clearInterval(ws._pingInterval);
    setConnected(false);
    if (socket === ws) {
      socket = null;
    }
    if (!currentToken) return;
    reconnectTimer = setTimeout(() => {
      reconnectDelay = Math.min(reconnectDelay * 2, MAX_DELAY);
      _connect(currentToken);
    }, reconnectDelay);
  };

  ws.onerror = () => {
    try {
      ws.close();
    } catch (_) {
      // no-op
    }
  };
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") _flushActiveGroups();
  });
}

export function connectWS(token) {
  currentToken = token;
  clearTimeout(reconnectTimer);
  _connect(token);
}

export function disconnectWS() {
  currentToken = null;
  setConnected(false);
  clearTimeout(reconnectTimer);
  activeGroups.clear();
  groupRefs.clear();
  if (socket) {
    const ws = socket;
    socket = null;
    clearInterval(ws._pingInterval);
    ws.close();
  }
}

/**
 * Subscribe to a WS event type.
 * @param {string} type  Event type (e.g. "gate_pass", "purchase", "lookup") or "*" for all.
 * @param {Function} cb  Callback receiving the raw event object.
 * @returns {Function}   Unsubscribe function.
 */
export function onEvent(type, cb) {
  if (!listeners.has(type)) listeners.set(type, new Set());
  listeners.get(type).add(cb);
  return () => offEvent(type, cb);
}

export function offEvent(type, cb) {
  const cbs = listeners.get(type);
  if (cbs) cbs.delete(cb);
}
